<script setup lang="ts">
import type { LineItem } from '~/types/shopware'
import { useCartLineItemUi } from '~/composables/useCartLineItemUi'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// price formatter provided via composable

const props = withDefaults(
  defineProps<{
    cartItem: LineItem
    maxQty?: number
  }>(),
  {
    maxQty: 100,
  },
)

const { cartItem } = toRefs(props)
const { isLoading, quantity, itemTotalPrice, isPromotion, itemUrl, cover, getFormattedPrice, removeCartItem } =
  useCartLineItemUi(cartItem)
</script>

<template>
  <div>
    <div class="label mobile">
      {{ t('shop.cartPage.productsTable.product') }}
    </div>
    <div class="title">
      <img
        v-if="!isPromotion && cover"
        :src="cover?.url"
        :alt="cover?.alt || cartItem?.label || t('shop.cart.productImage')"
        :title="cover?.title || cartItem?.label || t('shop.cart.productImage')"
      />
      <p class="text-small">
        <NuxtLink v-if="itemUrl" :to="itemUrl" class="product-title f-bold">
          {{ cartItem.label }}
        </NuxtLink>
        <br />
        <span class="product-number">
          {{ t('shop.cartPage.summaryTable.productNumber') }}
          {{ cartItem.payload.productNumber }}
        </span>
      </p>
    </div>
  </div>
  <div>
    <div class="label mobile">
      {{ t('shop.cartPage.productsTable.number') }}
    </div>
    <input
      v-if="!isPromotion"
      v-model="quantity"
      type="number"
      :disabled="isLoading"
      :min="cartItem.quantityInformation?.minPurchase || 1"
      :max="cartItem.quantityInformation?.maxPurchase || maxQty"
      :step="cartItem.quantityInformation?.purchaseSteps || 1"
      name="quantity"
    />
  </div>
  <div>
    <div class="label mobile">
      {{ t('shop.cartPage.productsTable.includingVAT') }}
    </div>
    <span v-for="(tax, index) in cartItem.price?.calculatedTaxes" :key="index"> {{ getFormattedPrice(tax.tax) }} </span>
  </div>
  <div>
    <div class="label mobile">
      {{ t('shop.cartPage.productsTable.total') }}
    </div>
    {{ getFormattedPrice(itemTotalPrice) }}
  </div>
  <div>
    <button type="button" :disabled="isLoading" :aria-label="t('remove item')" @click="removeCartItem">
      <Icon name="delete" />
    </button>
  </div>
</template>
<style scoped lang="scss">
.title {
  display: grid;
  grid-template-columns: 80px 1fr;
  align-items: center;
  gap: 24px;
  img {
    border-radius: 5px;
    box-shadow: var(--shadow);
  }
  p {
    margin-bottom: 0;
  }
  .product-title {
    color: var(--c-primary);
  }
  .product-number {
    color: var(--c-gray-75);
  }
}
input {
  width: 90px;
  height: 54px;
  border: 1px solid var(--c-line-light);
  padding: 16px;
  text-align: center;
}
@include breakpoint(ts) {
  .mobile {
    display: none;
  }
}
.label {
  margin-bottom: 10px;
}
</style>
